import { systemAccessLogHeader } from "@/constants/columnHeader";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { ActionHeader } from "@/features/standard/ActionHeader";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { Wrapper } from "@/features/standard/Wrapper";
import { CustomFilter } from "@/features/ui/Base/List/CustomFilter/CustomFilter";
import { CustomSorter } from "@/features/ui/Base/List/CustomSorter/CustomSorter";
import { SearchBox, SearchProps } from "@/features/ui/Base/List/SearchBox/SearchBox";
import useQueryString from "@/hooks/useQueryString";
import styled from "@emotion/styled";
import { Flex, Pagination } from "@mantine/core";
import { useState } from "react";
import type { Column } from "react-data-grid";
import DataGrid from "react-data-grid";

const SystemWrap = {
  Header: Header,
  HeaderTitle: HeaderTitle,
  HeaderSubTitle: HeaderSubTitle,
  ActionHeader: ActionHeader,
  Wrapper: Wrapper,
};

export const SystemPage = () => {
  const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);

  const [activePage, setPage] = useState(initialQueryString.page);
  const [query, setQuery] = useState(JSON.parse(initialQueryString.query));
  const [sort, setSort] = useState(initialQueryString.sort);
  const [search, setSearch] = useState<SearchProps>({
    search: initialQueryString.search,
    searchFields: initialQueryString.searchFields,
    pageSize: initialQueryString.pageSize,
  });

  const searchFieldsHeader = systemAccessLogHeader.filter(
    (item) => item.category === "text" && !item.isEnum,
  );

  const pageSize = 10;
  const totalRows = SystemPageMockupData.length;

  // 페이지에 맞는 데이터 슬라이싱
  const paginatedRows = SystemPageMockupData.slice(
    (activePage - 1) * pageSize,
    activePage * pageSize,
  );

  const columns: readonly Column<any>[] = [
    {
      key: "action",
      name: "동작",
      sortable: true,
      resizable: true,
    },
    {
      key: "company",
      name: "회사",
      sortable: true,
      resizable: true,
    },
    {
      key: "logDate",
      name: "날짜",
      sortable: true,
      resizable: true,
    },
    {
      key: "user",
      name: "유저",
      sortable: true,
      resizable: true,
    },
  ];

  return (
    <SystemWrap.Wrapper>
      <SystemWrap.Header>
        <SystemWrap.HeaderTitle>시스템 접근 로그</SystemWrap.HeaderTitle>
        <SystemWrap.HeaderSubTitle>
          {
            "사용자 또는 애플리케이션이 시스템에서 요청하는 개별 파일에 대한 전체 요청 목록을 기록합니다."
          }
        </SystemWrap.HeaderSubTitle>
      </SystemWrap.Header>
      <Flex w="100%" justify="space-between">
        <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
          <CustomFilter filterType={systemAccessLogHeader} setQuery={setQuery} query={query} />
          <CustomSorter sorterType={systemAccessLogHeader} setSort={setSort} sort={sort} />
        </Flex>
        <SearchBox searchType={searchFieldsHeader} setSearch={setSearch} search={search} />
      </Flex>
      <Flex w="100%" h="100%">
        <GridWrapper>
          <DataGrid
            columns={columns ?? []}
            rows={paginatedRows}
            rowHeight={40}
            rowKeyGetter={(row) => row.logDate}
          />
          <Pagination
            onChange={setPage}
            value={activePage}
            total={Math.ceil(totalRows / pageSize)} // 전체 페이지 계산
            size="lg"
            radius="sm"
            position="center"
          />
        </GridWrapper>
      </Flex>
    </SystemWrap.Wrapper>
  );
};

const SystemPageMockupData = [
  { action: "업데이트", company: "SIZL", logDate: "2025-06-20 15:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-20 14:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-20 13:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-20 12:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-20 11:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-20 10:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-20 09:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-20 08:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-20 07:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-20 06:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-20 05:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-20 04:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-20 03:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-20 02:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-20 01:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-20 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-29 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-29 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-29 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-29 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-29 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-29 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-29 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-29 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-29 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-29 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-29 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-29 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-29 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-29 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-29 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-29 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-29 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-29 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-29 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-29 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-29 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-29 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-29 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-29 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-28 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-28 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-28 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-28 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-28 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-28 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-28 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-28 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-28 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-28 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-28 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-28 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-28 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-28 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-28 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-28 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-28 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-28 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-28 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-28 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-28 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-28 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-28 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-28 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-27 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-27 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-27 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-27 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-27 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-27 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-27 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-27 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-27 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-27 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-27 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-27 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-27 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-27 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-27 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-27 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-27 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-27 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-27 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-27 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-27 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-27 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-27 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-27 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-26 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-26 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-26 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-26 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-26 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-26 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-26 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-26 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-26 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-26 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-26 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-26 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-26 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-26 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-26 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-26 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-26 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-26 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-26 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-26 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-26 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-26 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-26 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-26 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-25 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-25 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-25 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-25 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-25 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-25 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-25 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-25 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-25 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-25 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-25 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-25 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-25 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-25 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-25 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-25 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-25 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-25 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-25 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-25 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-25 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-25 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-25 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-25 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-24 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-24 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-24 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-24 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-24 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-24 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-24 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-24 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-24 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-24 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-24 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-24 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-24 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-24 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-24 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-24 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-24 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-24 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-24 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-24 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-24 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-24 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-24 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-24 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-23 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-23 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-23 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-23 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-23 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-23 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-23 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-23 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-23 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-23 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-23 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-23 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-23 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-23 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-23 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-23 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-23 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-23 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-23 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-23 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-23 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-23 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-23 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-23 00:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-@2 23:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-22 22:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-22 21:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-22 20:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-22 19:37:09", user: "user2" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-22 18:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-22 17:37:09", user: "supervisor" },
  { action: "추가", company: "SIZL", logDate: "2025-06-22 16:37:09", user: "admin" },
  { action: "업데이트", company: "Global Inc", logDate: "2025-06-22 15:37:09", user: "manager" },
  { action: "접속", company: "ABC Corp", logDate: "2025-06-22 14:37:09", user: "user1" },
  { action: "삭제", company: "SIZL", logDate: "2025-06-22 13:37:09", user: "admin" },
  { action: "업데이트", company: "XYZ Ltd", logDate: "2025-06-22 12:37:09", user: "supervisor" },
  { action: "접속", company: "Tech Solutions", logDate: "2025-06-22 11:37:09", user: "user2" },
  { action: "삭제", company: "Global Inc", logDate: "2025-06-22 10:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-22 09:37:09", user: "manager" },
  { action: "업데이트", company: "ABC Corp", logDate: "2025-06-22 08:37:09", user: "user1" },
  { action: "접속", company: "Global Inc", logDate: "2025-06-22 07:37:09", user: "admin" },
  { action: "삭제", company: "Tech Solutions", logDate: "2025-06-22 06:37:09", user: "supervisor" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-22 05:37:09", user: "user2" },
  { action: "접속", company: "SIZL", logDate: "2025-06-22 04:37:09", user: "admin" },
  { action: "추가", company: "XYZ Ltd", logDate: "2025-06-22 03:37:09", user: "manager" },
  { action: "삭제", company: "ABC Corp", logDate: "2025-06-22 02:37:09", user: "admin" },
  { action: "업데이트", company: "SIZL", logDate: "2025-06-22 01:37:09", user: "user1" },
  { action: "접속", company: "SIZL", logDate: "2025-06-22 00:37:09", user: "admin" },
];

const GridWrapper = styled.div`
  & *[role="grid"] {
    height: 440px;
    // --rdg-background-color: {(props) => (props.isDarkMode ? "white" : "none")};
    // --rdg-header-background-color: {(props) =>
    // props.isDarkMode ? "white" : "none"};
    // --rdg-color: {(props) => (props.isDarkMode ? "black" : "white")};
    // --rdg-row-hover-background-color: {(props) =>
    // props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;
